// iter453 — useDashboardStats hook.
// Owns all dashboard data state + the batched fetch chain that used
// to live inline in DashboardPage. Centralising it does three things:
//   • shrinks DashboardPage from ~611 → ~470 lines (pure refactor)
//   • makes the WS realtime hook wiring trivial — we just hand back
//     `refetch` and a stable ref the parent can hand off
//   • keeps the "fetch on branchFilter / dateFilter change" effect
//     beside the state it mutates, so the contract is local
//
// No behaviour change vs. the previous inline version: the same 16
// endpoints are hit in the same Promise.all batch, the same shaping
// logic produces dailyTrend / recentTransactions, and the same
// permission gate guards /invoices/dunning-scorecard.
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function useDashboardStats({ branchFilter, dateFilter, hasPermission }) {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [branchDues, setBranchDues] = useState(null);
  const [pendingSalaries, setPendingSalaries] = useState({
    employees: [], branch_summary: {},
    totals: { total_salary: 0, total_paid: 0, total_pending: 0 },
    period: '',
  });
  const [loading, setLoading] = useState(true);
  const [todayVsYest, setTodayVsYest] = useState(null);
  const [dailyTrend, setDailyTrend] = useState({ sales: [], expenses: [], profit: [] });
  const [predictiveData, setPredictiveData] = useState({
    lowStock: { items_at_risk: 0, forecasts: [] },
    peakHours: { peak_hours: [], total_transactions_analyzed: 0 },
    customerCLV: { high_value_customers: [], total_projected_revenue: 0 },
    profitTrend: { summary: {}, daily_breakdown: [] },
  });
  const [missingDataAlerts, setMissingDataAlerts] = useState([]);
  const [bankAccountSummary, setBankAccountSummary] = useState(null);
  const [anomalyHistory, setAnomalyHistory] = useState([]);
  const [budgetAlertsSummary, setBudgetAlertsSummary] = useState({
    total_active: 0, counts: {}, alerts: [],
  });
  const [stockAlertTrends, setStockAlertTrends] = useState({ trend: [], top_items: [], summary: {} });
  const [dunningScore, setDunningScore] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);

  const loadedGroups = useRef({});
  const groupLoadersRef = useRef({});

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (branchFilter.length > 0) params.set('branch_ids', branchFilter.join(','));
      if (dateFilter.start) params.set('start_date', dateFilter.start);
      if (dateFilter.end) params.set('end_date', dateFilter.end);
      const q = params.toString() ? `?${params.toString()}` : '';

      // Core KPIs only — Insights / Predictions / Upcoming load when their section is opened.
      const [statsRes, duesRes, pendRes, tvyRes, dsRes, mdRes, baRes, budgetAlertsRes] = await Promise.all([
        api.get(`/dashboard/stats${q}`).catch(() => ({ data: {} })),
        api.get('/reports/branch-dues-net').catch(() => ({ data: [] })),
        api.get('/employees/pending-summary').catch(() => ({ data: {} })),
        api.get(`/dashboard/today-vs-yesterday${q}`).catch(() => ({ data: {} })),
        api.get('/reports/daily-summary').catch(() => ({ data: [] })),
        api.get('/dashboard/missing-data-alerts').catch(() => ({ data: { alerts: [] } })),
        api.get('/bank-accounts/summary').catch(() => ({ data: null })),
        api.get('/accounting/budget-alerts/summary').catch(() => ({ data: { total_active: 0, counts: {}, alerts: [] } })),
      ]);
      Object.keys(loadedGroups.current).forEach((g) => { if (loadedGroups.current[g]) groupLoadersRef.current[g]?.(); });

      setStats(statsRes.data);
      setBranchDues(duesRes.data || []);
      setPendingSalaries(pendRes.data || {});
      setTodayVsYest(tvyRes.data || {});

      const days = (dsRes.data || []).slice(0, 7).reverse();
      setDailyTrend({
        sales: days.map(d => d.sales || 0),
        expenses: days.map(d => d.expenses || 0),
        profit: days.map(d => (d.sales || 0) - (d.expenses || 0)),
      });

      setMissingDataAlerts(mdRes.data?.alerts || []);
      if (baRes.data) setBankAccountSummary(baRes.data);
      setBudgetAlertsSummary(budgetAlertsRes.data || { total_active: 0, counts: {}, alerts: [] });

      // Build recent transactions from stats payload.
      const recentSales = (statsRes.data?.recent_sales || []).map(s => ({ ...s, txType: 'sale' }));
      const recentExpenses = (statsRes.data?.recent_expenses || []).map(e => ({ ...e, txType: 'expense' }));
      const combined = [...recentSales, ...recentExpenses]
        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        .slice(0, 8);
      setRecentTransactions(combined);
    } catch {
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  }, [branchFilter, dateFilter, hasPermission]);

  const GROUP_LOADERS = {
    insights: () => api.get('/anomaly-detection/history').then(r => setAnomalyHistory((r.data || []).slice(0, 1))).catch(() => {}),
    predictions: () => {
      Promise.all([
        api.get('/predictions/inventory-demand').catch(() => ({ data: { items_at_risk: 0, forecasts: [] } })),
        api.get('/predictions/peak-hours').catch(() => ({ data: { peak_hours: [], total_transactions_analyzed: 0 } })),
        api.get('/predictions/customer-clv').catch(() => ({ data: { high_value_customers: [], total_projected_revenue: 0 } })),
        api.get('/predictions/profit-decomposition').catch(() => ({ data: { summary: {}, daily_breakdown: [] } })),
        api.get('/dashboard/stock-alert-trends?days=30').catch(() => ({ data: { trend: [], top_items: [], summary: {} } })),
      ]).then(([low, peak, clv, profit, trends]) => {
        setPredictiveData({ lowStock: low.data, peakHours: peak.data, customerCLV: clv.data, profitTrend: profit.data });
        setStockAlertTrends(trends.data || { trend: [], top_items: [], summary: {} });
      });
      // Dunning scorecard — gated to avoid 403 spam for users without invoices:read.
      if (hasPermission && hasPermission('invoices')) {
        api.get('/invoices/dunning-scorecard?lookback_days=90').then(r => setDunningScore(r.data)).catch(() => setDunningScore(null));
      }
    },
    upcoming: () => api.get('/documents/alerts/upcoming').then(r => setAlerts(r.data || [])).catch(() => {}),
  };
  groupLoadersRef.current = GROUP_LOADERS;
  const loadGroup = (name) => {
    if (loadedGroups.current[name]) return;
    loadedGroups.current[name] = true;
    GROUP_LOADERS[name]();
  };

  // Stable ref so consumers (WS realtime hook) can call the latest fn
  // without re-subscribing on every render.
  const fetchStatsRef = useRef(fetchStats);
  useEffect(() => { fetchStatsRef.current = fetchStats; }, [fetchStats]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, dateFilter]);

  // Allows the WS realtime hook to refresh just the budget-alerts slice
  // without re-triggering the full batch.
  const refreshBudgetAlerts = useCallback(() => {
    api.get('/accounting/budget-alerts/summary')
      .then(r => setBudgetAlertsSummary(r.data || { total_active: 0, counts: {}, alerts: [] }))
      .catch(() => {});
  }, []);

  return {
    // data
    stats, alerts, branchDues, pendingSalaries, todayVsYest, dailyTrend,
    predictiveData, missingDataAlerts, bankAccountSummary, anomalyHistory,
    budgetAlertsSummary, stockAlertTrends, dunningScore, recentTransactions,
    // status
    loading,
    // actions
    fetchStats, fetchStatsRef, refreshBudgetAlerts, loadGroup,
  };
}

export default useDashboardStats;
